import { memo, useEffect, useRef } from 'react';
import { calculateBearing } from '../member/utils/map.utils';
import L from 'leaflet';
import { useMap } from 'react-leaflet';
import { MovingMarker } from '../member/utils/MovingMarker';
import { RotatingIcon } from '../member/utils/RotatingIcon';
import 'leaflet-routing-machine';

import { TripLegAddress } from '../../data/models/reservation.model';
import { DriverLocation } from '../../data/models/driver-location.model';
import { useTheme } from '@mui/material';

const {
  __environment__: { REACT_APP_MAPBOX_KEY },
} = window as any;

// Polling interval for driver location updates
const POLLING_INTERVAL = 90000;

const pickupIcon = L.icon({
  iconUrl: '/pickup_marker.svg',
  className: 'pickup_icon',
  iconSize: [35, 35],
  iconAnchor: [20, 30],
});

const dropoffIcon = L.icon({
  iconUrl: '/dropoff_marker.svg',
  className: 'dropoff_icon',
  iconSize: [35, 35],
  iconAnchor: [10, 30],
});

const driverIcon = new RotatingIcon({
  iconUrl: '/car_icon.png',
  className: 'driver_icon',
  iconSize: [45, 42],
  iconAnchor: [22, 21],
  rotationOffset: 46.6,
  translationOffset: [-43, -40],
});

const getRoutePaddingRatio = (bounds: L.LatLngBounds) => {
  const northEast = bounds.getNorthEast();
  const southWest = bounds.getSouthWest();
  const latitudeSpan = Math.abs(northEast.lat - southWest.lat);
  const longitudeSpan = Math.abs(northEast.lng - southWest.lng);
  const routeSpan = Math.max(latitudeSpan, longitudeSpan);

  if (routeSpan < 0.01) return 0.4;
  if (routeSpan < 0.05) return 0.25;
  if (routeSpan < 0.15) return 0.18;
  return 0.12;
};

/** Tear down routing control without stale async callbacks touching a null map. */
const disposeRoutingControl = (control: L.Routing.Control | undefined) => {
  if (!control) return;

  const routingControl = control as L.Routing.Control & {
    _requestCount?: number;
    _pendingRequest?: { abort?: () => void } | null;
  };

  // leaflet-routing-machine route callbacks only check request id, not _map.
  routingControl._requestCount = (routingControl._requestCount ?? 0) + 1;

  if (routingControl._pendingRequest?.abort) {
    routingControl._pendingRequest.abort();
    routingControl._pendingRequest = null;
  }

  try {
    control.remove();
  } catch {
    // Map may already be destroyed during React strict-mode remount.
  }
};

interface MapWithNavigationProps {
  tripLegs: Array<{
    pickupAddress: TripLegAddress;
    dropoffAddress: TripLegAddress;
  }>;
  driverLocation: DriverLocation | null;
  fitBounds?: boolean;
}

export default memo(
  function MapWithNavigation({
    tripLegs,
    driverLocation,
    fitBounds = false,
  }: MapWithNavigationProps) {
    const map = useMap();
    const theme = useTheme();
    const routingMachine = useRef<L.Routing.Control>();
    const layerGroup = useRef<L.LayerGroup>();
    const prevDriverLocRef = useRef<L.LatLng | null>(null);
    const movingMarker = useRef<MovingMarker | null>(null);
    const hasInitializedMap = useRef(false);

    useEffect(() => {
      if (!map) return;

      map.attributionControl.setPrefix(false);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      }).addTo(map);
    }, [map]);

    useEffect(() => {
      if (!map || hasInitializedMap.current) return;

      if (tripLegs && tripLegs.length > 0) {
        const routeBounds = L.latLngBounds([]);

        tripLegs.forEach((tripLeg) => {
          const { pickupAddress, dropoffAddress } = tripLeg;

          if (
            pickupAddress &&
            pickupAddress.latitude &&
            pickupAddress.longitude &&
            dropoffAddress &&
            dropoffAddress.latitude &&
            dropoffAddress.longitude
          ) {
            const pickup = L.latLng(
              pickupAddress.latitude,
              pickupAddress.longitude,
            );
            const dropoff = L.latLng(
              dropoffAddress.latitude,
              dropoffAddress.longitude,
            );

            routeBounds.extend(pickup);
            routeBounds.extend(dropoff);

            if (layerGroup.current) {
              layerGroup.current.remove();
            }
            layerGroup.current = L.layerGroup().addTo(map);

            if (routingMachine.current) {
              routingMachine.current.setWaypoints([pickup, dropoff]);
            } else {
              routingMachine.current = L.Routing.control({
                waypoints: [pickup, dropoff],
                router: L.Routing.mapbox(REACT_APP_MAPBOX_KEY, {}),
                routeWhileDragging: false,
                // @ts-ignore
                addWaypoints: false,
                // @ts-ignore
                draggableWaypoints: false,
                lineOptions: {
                  styles: [
                    {
                      color: theme.palette.primary.main,
                      opacity: 1,
                      weight: 5,
                    },
                  ],
                  extendToWaypoints: false,
                  missingRouteTolerance: 0,
                },
                // @ts-ignore
                createMarker: () => null,
              });
              routingMachine.current.addTo(map);
            }

            L.marker(pickup, { icon: pickupIcon }).addTo(layerGroup.current);
            L.marker(dropoff, { icon: dropoffIcon }).addTo(layerGroup.current);
          }
        });

        if (routeBounds.isValid() && fitBounds) {
          map.fitBounds(routeBounds.pad(getRoutePaddingRatio(routeBounds)), {
            maxZoom: 16,
          });
        }

        hasInitializedMap.current = true;
      }
    }, [map, tripLegs]);

    useEffect(() => {
      // Clean up driver marker if no valid driver location
      if (!map || !layerGroup.current || !driverLocation) {
        if (movingMarker.current) {
          movingMarker.current.remove();
          movingMarker.current = null;
          prevDriverLocRef.current = null;
        }
        return;
      }

      const lat = parseFloat(driverLocation.latitude);
      const lng = parseFloat(driverLocation.longitude);

      if (!lat || !lng) {
        if (movingMarker.current) {
          movingMarker.current.remove();
          movingMarker.current = null;
          prevDriverLocRef.current = null;
        }
        return;
      }

      const driverLoc = L.latLng(lat, lng);

      let bearing = 0;
      if (prevDriverLocRef.current && driverLoc) {
        bearing = calculateBearing(prevDriverLocRef.current, driverLoc);
      }

      if (!movingMarker.current) {
        // @ts-ignore
        movingMarker.current = new MovingMarker(driverLoc, { icon: driverIcon })
          // @ts-ignore
          .addTo(layerGroup.current);
        movingMarker.current.setLatLng(driverLoc);
        movingMarker.current.setRotationAngle(bearing);
      } else {
        movingMarker.current.setLatLng(driverLoc);
        movingMarker.current.setRotationAngle(bearing);
      }

      prevDriverLocRef.current = driverLoc;
    }, [map, driverLocation]);

    // Remove map elements when component unmounts
    useEffect(() => {
      return () => {
        if (movingMarker.current) {
          movingMarker.current.remove();
          movingMarker.current = null;
        }
        disposeRoutingControl(routingMachine.current);
        routingMachine.current = undefined;
        if (layerGroup.current) {
          layerGroup.current.remove();
          layerGroup.current = undefined;
        }
      };
    }, []);

    return null;
  },
  (prevProps, nextProps) => {
    return (
      prevProps.driverLocation?.estimatedTimeOfArrivalSeconds ===
        nextProps.driverLocation?.estimatedTimeOfArrivalSeconds &&
      prevProps.driverLocation?.latitude ===
        nextProps.driverLocation?.latitude &&
      prevProps.driverLocation?.longitude ===
        nextProps.driverLocation?.longitude &&
      prevProps.tripLegs === nextProps.tripLegs
    );
  },
);
